const NARA_AUTH_STORAGE = 'nara.auth.session';
const NARA_ACCOUNT_API = '/functions/v1/nara-web-account';
const NARA_AUTH_SAFE_PATHS = [
  '/pages/my-nara',
  '/pages/login',
  '/pages/reset-password',
  '/pages/membership',
  '/pages/invite',
  '/invite',
  '/pages/gift',
  '/gift',
  '/pages/redeem',
  '/redeem',
  '/pages/gift-admin',
  '/pages/checkout',
  '/checkout',
  '/pages/membership-success',
  '/',
];

function naraAuthConfig(root) {
  const url = (root?.dataset.supabaseUrl || '').replace(/\/$/, '');
  const key = root?.dataset.supabaseAnonKey || '';
  return { url, key, ready: Boolean(url && key) };
}

function naraAuthIsThemeEditor() {
  try {
    if (window.Shopify?.designMode || window.Shopify?.inspectMode) return true;
    const params = new URLSearchParams(window.location.search);
    if (params.has('preview_theme_id')) return true;
  } catch {
    /* ignore */
  }
  return false;
}

function naraAuthSafeNext(value) {
  if (!value || typeof value !== 'string') return '/pages/my-nara';
  try {
    const path = value.startsWith('http') ? new URL(value).pathname : value.split('#')[0];
    if (!path.startsWith('/') || path.startsWith('//')) return '/pages/my-nara';
    if (NARA_AUTH_SAFE_PATHS.some((allowed) => path === allowed || path.startsWith(`${allowed}?`))) {
      return path;
    }
  } catch {
    /* ignore */
  }
  return '/pages/my-nara';
}

function naraAuthRequestedNext(fallback) {
  const params = new URLSearchParams(window.location.search);
  return naraAuthSafeNext(params.get('next') || fallback || '/pages/my-nara');
}

function naraAuthReadSession() {
  try {
    const raw = window.localStorage.getItem(NARA_AUTH_STORAGE);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function naraAuthWriteSession(session) {
  if (!session?.access_token) {
    window.localStorage.removeItem(NARA_AUTH_STORAGE);
    return;
  }
  window.localStorage.setItem(NARA_AUTH_STORAGE, JSON.stringify(session));
}

function naraAuthClear() {
  window.localStorage.removeItem(NARA_AUTH_STORAGE);
}

async function naraAuthRequest(config, path, { method = 'POST', body, token, headers: extra } = {}) {
  const headers = {
    apikey: config.key,
    'Content-Type': 'application/json',
    ...extra,
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  else headers.Authorization = `Bearer ${config.key}`;

  const response = await fetch(`${config.url}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  return { ok: response.ok, status: response.status, payload };
}

const NARA_NAME_SKIP = new Set([
  'athlete', 'user', 'guest', 'nara', 'admin', 'unknown', 'contact', 'hello',
  'info', 'support', 'team', 'mail', 'office', 'noreply', 'no-reply',
]);

function naraFirstNameOf(value) {
  if (!value || typeof value !== 'string' || value.includes('@') || /https?:/i.test(value)) return '';
  const token = value.trim().split(/[\s._-]+/)[0].replace(/[^\p{L}'-]/gu, '');
  if (token.length < 2 || token.length > 40) return '';
  if (NARA_NAME_SKIP.has(token.toLowerCase())) return '';
  return token.charAt(0).toLocaleUpperCase('en-US') + token.slice(1);
}

function naraNameCandidates(value, depth = 0) {
  if (depth > 4 || value == null) return [];
  if (typeof value === 'string') return value.trim() ? [value.trim()] : [];
  if (Array.isArray(value)) return value.flatMap((item) => naraNameCandidates(item, depth + 1));
  if (typeof value !== 'object') return [];
  const keys = [
    'first_name', 'firstName', 'given_name', 'givenName', 'abx_first_name', 'abx_name',
    'prenom', 'display_name', 'displayName', 'full_name', 'fullName', 'name',
    'identity_data', 'user_metadata', 'raw_user_meta_data', 'app_metadata', 'identities', 'user',
  ];
  return keys.flatMap((key) => (key in value ? naraNameCandidates(value[key], depth + 1) : []));
}

function naraNameFromEmail(email) {
  if (!email || typeof email !== 'string' || !email.includes('@')) return '';
  return naraFirstNameOf(email.split('@')[0]);
}

function naraJwtClaims(token) {
  if (!token || typeof token !== 'string') return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function naraFirstName(...values) {
  for (const value of values) {
    for (const candidate of naraNameCandidates(value)) {
      const name = naraFirstNameOf(candidate);
      if (name) return name;
    }
  }
  for (const value of values) {
    const email = typeof value === 'string' && value.includes('@')
      ? value
      : value && typeof value === 'object'
        ? value.email
        : '';
    const name = naraNameFromEmail(email);
    if (name) return name;
  }
  return '';
}

function naraAuthGenericError() {
  return 'We couldn’t sign you in. Check your details and try again.';
}

function naraAuthMessage(payload) {
  const code = payload?.error_code || payload?.error || '';
  if (code === 'email_not_confirmed') {
    return 'This account still needs email confirmation. Open the message from Nara to continue.';
  }
  if (payload?.msg && /expired|invalid claim|session/i.test(payload.msg)) {
    return 'This session has expired. Sign in again.';
  }
  return naraAuthGenericError();
}

function naraAuthUpdateHeader() {
  const session = naraAuthReadSession();
  document.querySelectorAll('[data-nara-auth-entry]').forEach((link) => {
    if (session?.access_token) {
      link.textContent = 'My Nara';
      link.setAttribute('href', '/pages/my-nara');
    } else if (link.dataset.naraAuthLoginHref) {
      link.textContent = 'Login';
      link.setAttribute('href', link.dataset.naraAuthLoginHref);
    }
  });
}

function naraAuthHasRecoveryHash() {
  const hash = window.location.hash || '';
  return hash.includes('type=recovery') || hash.includes('access_token=');
}

async function naraAuthCaptureRecovery(config) {
  const raw = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  if (!raw) return false;
  const params = new URLSearchParams(raw);
  const access = params.get('access_token');
  const refresh = params.get('refresh_token');
  if (!access || (params.get('type') && params.get('type') !== 'recovery')) return false;

  naraAuthWriteSession({
    access_token: access,
    refresh_token: refresh,
    token_type: params.get('token_type') || 'bearer',
    user: null,
  });

  if (config?.ready) {
    const { ok, payload } = await naraAuthRequest(config, '/auth/v1/user', {
      method: 'GET',
      token: access,
    });
    if (ok && payload) {
      const session = naraAuthReadSession() || {};
      session.user = payload;
      naraAuthWriteSession(session);
    }
  }

  window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
  return true;
}

class NaraAuthForm extends HTMLElement {
  connectedCallback() {
    this.config = naraAuthConfig(this);
    this.abort = new AbortController();
    const { signal } = this.abort;

    const canRecover = this.dataset.view !== 'login';
    if (canRecover && naraAuthHasRecoveryHash()) {
      this.dataset.view = 'update';
      this.revealUpdateView();
      naraAuthCaptureRecovery(this.config);
    }

    this.view = this.dataset.view || 'login';
    this.status = this.querySelector('form:not([hidden]) [data-nara-auth-status]') || this.querySelector('[data-nara-auth-status]');
    this.form = this.querySelector('form:not([hidden])') || this.querySelector('form');
    this.submitBtn = this.form?.querySelector('[data-nara-auth-submit]');

    this.querySelectorAll('form').forEach((form) => {
      form.addEventListener('submit', (event) => this.onSubmit(event), { signal });
    });
    this.querySelectorAll('[data-nara-auth-toggle]').forEach((toggle) => {
      const field = toggle.closest('.nara-auth__password')?.querySelector('[data-nara-auth-password]');
      if (!field) return;
      toggle.addEventListener(
        'click',
        () => {
          this.password = field;
          this.toggle = toggle;
          this.togglePassword();
        },
        { signal }
      );
    });

    if (!this.config.ready) {
      this.show('Sign-in isn’t connected on this website yet.', 'error');
      this.querySelectorAll('[data-nara-auth-submit]').forEach((button) => {
        button.disabled = true;
      });
    }

    if (this.view === 'login' && naraAuthReadSession()?.access_token) {
      window.location.replace(naraAuthRequestedNext(this.dataset.next || '/pages/my-nara'));
    }
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  revealUpdateView() {
    const request = this.querySelector('.nara-auth__form--request');
    const update = this.querySelector('.nara-auth__form--update');
    if (request) request.hidden = true;
    if (update) update.hidden = false;

    const section = this.closest('.nara-auth');
    const requestLede = section?.querySelector('.nara-auth__lede--request');
    const updateLede = section?.querySelector('.nara-auth__lede--update');
    const title = section?.querySelector('.nara-auth__title');
    if (requestLede) requestLede.hidden = true;
    if (updateLede) updateLede.hidden = false;
    if (title && this.dataset.updateHeading) title.textContent = this.dataset.updateHeading;
  }

  show(message, tone) {
    if (!this.status) return;
    this.status.hidden = !message;
    this.status.textContent = message || '';
    this.status.dataset.tone = tone || 'info';
  }

  togglePassword() {
    const hidden = this.password.type === 'password';
    this.password.type = hidden ? 'text' : 'password';
    this.toggle.setAttribute('aria-pressed', hidden ? 'true' : 'false');
    this.toggle.setAttribute('aria-label', hidden ? 'Hide password' : 'Show password');
    this.toggle.textContent = hidden ? 'Hide' : 'Show';
  }

  async onSubmit(event) {
    event.preventDefault();
    const form = event.target;
    const submitBtn = form.querySelector('[data-nara-auth-submit]');
    this.form = form;
    this.submitBtn = submitBtn;
    this.status = form.querySelector('[data-nara-auth-status]') || this.status;
    this.view = this.dataset.view || this.view;

    if (!this.config.ready) return;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    if (form.dataset.submitting === 'true') return;

    form.dataset.submitting = 'true';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.dataset.label = submitBtn.textContent;
      if (this.view !== 'login') submitBtn.textContent = 'Sending…';
    }
    this.show('', 'info');

    try {
      if (this.view === 'forgot') await this.sendReset();
      else if (this.view === 'update') await this.updatePassword();
      else await this.signIn();
    } catch {
      this.show(
        this.view === 'forgot'
          ? 'The reset email could not be sent. Try again.'
          : naraAuthGenericError(),
        'error'
      );
    } finally {
      form.dataset.submitting = 'false';
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = submitBtn.dataset.label || submitBtn.textContent;
      }
    }
  }

  async signIn() {
    const email = this.form.elements.email.value.trim();
    const password = this.form.elements.password.value;
    const { ok, payload } = await naraAuthRequest(this.config, '/auth/v1/token?grant_type=password', {
      body: { email, password },
    });
    if (!ok || !payload?.access_token) {
      this.show(naraAuthMessage(payload), 'error');
      return;
    }
    naraAuthWriteSession(payload);
    naraAuthUpdateHeader();
    try {
      const stored = window.sessionStorage.getItem('nara.referral.code');
      if (stored && payload.access_token) {
        await naraAuthRequest(this.config, '/functions/v1/nara-referral', {
          token: payload.access_token,
          body: { action: 'claim', code: stored },
        });
      }
    } catch {
      /* claim is server-validated; ignore local failure */
    }
    window.location.assign(naraAuthRequestedNext(this.dataset.next || '/pages/my-nara'));
  }

  async sendReset() {
    const email = this.form.elements.email.value.trim();
    const redirectTo = `${window.location.origin}/pages/reset-password`;
    const { ok } = await naraAuthRequest(this.config, '/auth/v1/recover', {
      body: { email },
      headers: { 'Redirect-To': redirectTo },
    });
    if (!ok) {
      this.show('The reset email could not be sent. Try again.', 'error');
      return;
    }
    this.show('If an account exists for this email, you’ll receive a reset link.', 'success');
  }

  async updatePassword() {
    const password = this.form.elements.password.value;
    const confirm = this.form.elements.confirm?.value;
    if (confirm && confirm !== password) {
      this.show('The new passwords do not match.', 'error');
      return;
    }
    const session = naraAuthReadSession();
    if (!session?.access_token) {
      this.show('Open the reset link from your email to choose a new password.', 'error');
      return;
    }
    const { ok, payload } = await naraAuthRequest(this.config, '/auth/v1/user', {
      method: 'PUT',
      token: session.access_token,
      body: { password },
    });
    if (!ok) {
      this.show(payload?.msg || 'The password could not be updated. Request a new reset link.', 'error');
      return;
    }
    naraAuthClear();
    naraAuthUpdateHeader();
    this.show('Your password has been updated. You can sign in now.', 'success');
    window.setTimeout(() => {
      window.location.assign('/pages/login');
    }, 1200);
  }
}

class NaraAccountSpace extends HTMLElement {
  connectedCallback() {
    this.config = naraAuthConfig(this);
    this.status = this.querySelector('[data-nara-account-status]');
    this.email = this.querySelector('[data-nara-account-email]');
    this.logout = this.querySelector('[data-nara-auth-logout]');
    this.abort = new AbortController();
    this.logout?.addEventListener('click', () => this.signOut(), { signal: this.abort.signal });
    this.boot();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  setStatus(message) {
    if (this.status) {
      this.status.hidden = !message;
      this.status.textContent = message || '';
    }
  }

  setText(selector, value) {
    const node = this.querySelector(selector);
    if (node) node.textContent = value || '';
  }

  fillFacts(selector, entries) {
    const root = this.querySelector(selector);
    if (!root) return;
    root.replaceChildren();
    const rows = (entries || []).filter((row) => row && row[1]);
    root.hidden = rows.length === 0;
    rows.forEach(([label, value]) => {
      const wrap = document.createElement('div');
      const dt = document.createElement('dt');
      dt.textContent = label;
      const dd = document.createElement('dd');
      dd.textContent = value;
      wrap.append(dt, dd);
      root.append(wrap);
    });
  }

  fillList(selector, items) {
    const root = this.querySelector(selector);
    if (!root) return;
    root.replaceChildren();
    const rows = (items || []).filter(Boolean);
    root.hidden = rows.length === 0;
    rows.forEach((item) => {
      const li = document.createElement('li');
      li.textContent = item;
      root.append(li);
    });
  }

  fillWorkoutCarousel(workouts) {
    const wrap = this.querySelector('[data-nara-account-workouts]');
    const root = this.querySelector('[data-nara-account-workout-carousel]');
    if (!wrap || !root) return;
    root.replaceChildren();
    const rows = (workouts || []).filter((row) => row && (row.title || row.at));
    wrap.hidden = rows.length === 0;
    rows.forEach((row, index) => {
      const card = document.createElement('article');
      card.className = 'nara-account__workout-card';
      card.setAttribute('aria-label', `${row.title || 'Workout'}, completed`);

      const eyebrow = document.createElement('p');
      eyebrow.className = 'nara-account__workout-eyebrow';
      eyebrow.textContent = 'Today’s Workout';

      const title = document.createElement('h3');
      title.className = 'nara-account__workout-title';
      title.textContent = row.title || 'Workout';

      const description = document.createElement('p');
      description.className = 'nara-account__workout-desc';
      const moves = (row.exercises || []).filter(Boolean);
      description.textContent = moves.length ? moves.join(' · ') : (row.muscles || []).filter(Boolean).join(' · ');
      description.hidden = !description.textContent;

      const meta = document.createElement('p');
      meta.className = 'nara-account__workout-meta';
      const bits = [];
      if (row.duration_min) bits.push(`${row.duration_min} min`);
      if (row.exercise_count) bits.push(`${row.exercise_count} exercise${row.exercise_count === 1 ? '' : 's'}`);
      if (row.at) bits.push(this.formatDate(row.at));
      meta.textContent = bits.join(' · ');
      meta.hidden = !bits.length;

      const done = document.createElement('p');
      done.className = 'nara-account__workout-done';
      done.textContent = 'Completed';

      card.append(eyebrow, title);
      if (!description.hidden) card.append(description);
      if (!meta.hidden) card.append(meta);
      card.append(done);
      card.style.setProperty('--nara-workout-index', String(index + 1));
      root.append(card);
    });
  }

  formatDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }

  applyHeading(firstName) {
    const heading = this.querySelector('[data-nara-account-heading]');
    if (!heading) return;
    const base = heading.getAttribute('data-nara-account-heading-base') || 'Welcome back.';
    heading.textContent = firstName
      ? `${base.replace(/[.!]+$/, '')}, ${firstName}.`
      : base;
  }

  showPhoto(url, name) {
    const wrap = this.querySelector('[data-nara-account-photo]');
    const img = this.querySelector('[data-nara-account-photo-img]');
    const initials = this.querySelector('[data-nara-account-initials]');
    if (!wrap || !img) return;
    const safe = typeof url === 'string' && /^https:\/\//i.test(url) ? url : '';
    const mark = (name || '').trim().charAt(0).toLocaleUpperCase('en-US');
    const showInitials = () => {
      img.hidden = true;
      img.removeAttribute('src');
      if (initials && mark) {
        initials.hidden = false;
        initials.textContent = mark;
        wrap.hidden = false;
        return;
      }
      if (initials) initials.hidden = true;
      wrap.hidden = true;
    };
    if (!safe) {
      showInitials();
      return;
    }
    img.alt = name ? `${name}’s profile photo` : 'Profile photo';
    img.onerror = () => showInitials();
    img.onload = () => {
      img.hidden = false;
      if (initials) initials.hidden = true;
      wrap.hidden = false;
    };
    img.src = safe;
    wrap.hidden = false;
  }

  async boot() {
    const session = naraAuthReadSession();
    if (!session?.access_token) {
      if (naraAuthIsThemeEditor()) {
        this.setStatus('Theme editor preview. Log in on the live site to see this account. You can still edit settings here.');
        return;
      }
      window.location.replace('/pages/login?next=/pages/my-nara');
      return;
    }
    if (!this.config.ready) {
      this.setStatus('This account space isn’t connected on the website yet.');
      return;
    }

    this.setStatus('Checking your session…');
    try {
      const { ok, status, payload } = await naraAuthRequest(this.config, '/auth/v1/user', {
        method: 'GET',
        token: session.access_token,
      });
      if (status === 401 || status === 403) {
        naraAuthClear();
        naraAuthUpdateHeader();
        window.location.replace('/pages/login?next=/pages/my-nara');
        return;
      }
      if (!ok || !payload?.id) {
        this.setStatus('We couldn’t load your account.');
        return;
      }
      if (this.email) this.email.textContent = payload.email || 'Signed in';
      this.authUser = payload;
      this.applyHeading(naraFirstName(
        payload,
        session.user,
        naraJwtClaims(session.access_token),
        payload.email,
        session.user?.email,
      ));
      this.setStatus('');
      await this.loadAppAccount(session.access_token);
    } catch {
      this.setStatus('We couldn’t load your account.');
    }
  }

  async loadAppAccount(token) {
    const { ok, payload } = await naraAuthRequest(this.config, NARA_ACCOUNT_API, {
      method: 'GET',
      token,
    });
    if (!ok || !payload?.ok) {
      this.setText('[data-nara-account-profile]', 'Your email is connected. App details could not be loaded right now.');
      this.setText('[data-nara-account-program]', 'Your program stays in the Nara app until this page can read it.');
      this.setText('[data-nara-account-goals]', 'Your goals stay in the Nara app until this page can read them.');
      this.setText('[data-nara-account-progress]', 'Your progress stays in the Nara app until this page can read it.');
      this.setText('[data-nara-account-membership]', 'Membership can be billed on the website through Stripe, or in the Nara app through the App Store or Google Play.');
      return;
    }
    this.renderAccount(payload);
  }

  renderAccount(data) {
    const profile = data.profile || {};
    const program = data.program || {};
    const goals = data.goals || {};
    const progress = data.progress || {};
    const membership = data.membership || {};

    const firstName = naraFirstName(
      profile.first_name,
      profile.display_name,
      profile.email,
      this.authUser,
      naraAuthReadSession()?.user,
    );
    this.showPhoto(profile.photo_url, firstName || profile.display_name);
    this.applyHeading(firstName);

    this.setText(
      '[data-nara-account-profile]',
      firstName || profile.display_name
        ? `${firstName || profile.display_name} · same Nara account as the app.`
        : 'Same Nara account as the app.'
    );
    this.fillFacts('[data-nara-account-profile-facts]', [
      ['Email', profile.email],
      ['Member since', this.formatDate(profile.member_since)],
      ['Level', profile.level],
      ['Achievements', Number.isFinite(Number(profile.achievements_count)) ? String(profile.achievements_count) : ''],
      ['Challenges completed', Number.isFinite(Number(profile.challenges_completed)) ? String(profile.challenges_completed) : ''],
      ['Trains at', (profile.locations || []).join(', ')],
      ['Equipment', profile.equipment_count ? `${profile.equipment_count} saved${profile.equipment_locations?.length ? ` · ${(profile.equipment_locations || []).join(', ')}` : ''}` : ''],
    ]);

    const lastSession = program.last_session;
    const completedWorkouts = program.workouts || [];
    this.setText(
      '[data-nara-account-program]',
      completedWorkouts.length
        ? 'Workouts completed in the Nara app.'
        : program.level || (program.workout_types || []).length || lastSession
          ? 'Pulled from your current Nara training context.'
          : 'No completed workouts yet. Finish a Today’s Workout in the app and it will appear here.'
    );
    this.fillFacts('[data-nara-account-program-facts]', [
      ['Level', program.level],
      ['Focus', (program.workout_types || []).join(', ')],
      ['Typical session', program.avg_session_minutes ? `${program.avg_session_minutes} min` : ''],
    ]);
    this.fillWorkoutCarousel(completedWorkouts);

    const goalSeen = new Set();
    const goalItems = [
      ...(goals.training || []),
      ...(goals.personal || []).map((row) => row.title),
    ].filter((title) => {
      const key = String(title || '').trim().toLowerCase();
      if (!key || goalSeen.has(key)) return false;
      goalSeen.add(key);
      return true;
    });
    this.setText(
      '[data-nara-account-goals]',
      goalItems.length
        ? goalItems.join(' · ')
        : 'No goals are saved on this account yet. Add them in the app and they will show here.'
    );
    this.fillList('[data-nara-account-goals-list]', []);

    const recent = (progress.recent_sessions || []).map((row) =>
      [row.title || 'Session', this.formatDate(row.at), row.status].filter(Boolean).join(' · ')
    );
    this.setText(
      '[data-nara-account-progress]',
      progress.sessions_logged || progress.last_activity_at || recent.length
        ? 'Activity synced from the Nara app.'
        : 'No workout history is synced to this account yet.'
    );
    this.fillFacts('[data-nara-account-progress-facts]', [
      ['Sessions logged', progress.sessions_logged ? String(progress.sessions_logged) : ''],
      ['Last activity', this.formatDate(progress.last_activity_at)],
      ['Days between sessions', progress.avg_days_between_workouts ? String(progress.avg_days_between_workouts) : ''],
    ]);
    this.fillList('[data-nara-account-progress-list]', []);

    if (membership.linked && membership.plan) {
      this.setText(
        '[data-nara-account-membership]',
        membership.stripe
          ? 'Billed on the Nara website through Stripe. The same membership unlocks the app.'
          : membership.gift
            ? 'This membership was activated from a Nara gift.'
            : 'Billed in the Nara app. This is the plan linked to your account.'
      );
    } else {
      this.setText(
        '[data-nara-account-membership]',
        'No membership is linked to this account yet. Choose a plan to subscribe on the website, or in the Nara app.'
      );
    }
    const appleNote = this.querySelector('[data-nara-account-apple-note]');
    if (appleNote) appleNote.hidden = !membership.apple_managed;
    const portal = this.querySelector('[data-nara-billing-portal]');
    if (portal) portal.hidden = !membership.stripe;
    const subscribe = this.querySelector('[data-nara-billing-subscribe]');
    if (subscribe) subscribe.textContent = membership.linked ? 'Change membership' : 'Subscribe';
    this.fillFacts('[data-nara-account-membership-facts]', [
      ['Plan', membership.plan],
      ['Billing', membership.interval],
      ['Status', membership.in_trial ? 'Trial' : membership.status],
      ['Through', membership.billing_channel],
      ['Current period ends', this.formatDate(membership.current_period_end)],
      ['Trial ends', this.formatDate(membership.trial_ends_at)],
    ]);
  }

  async signOut() {
    const session = naraAuthReadSession();
    try {
      if (session?.access_token && this.config.ready) {
        await naraAuthRequest(this.config, '/auth/v1/logout', {
          token: session.access_token,
        });
      }
    } catch {
      /* still clear the local session */
    }
    naraAuthClear();
    naraAuthUpdateHeader();
    window.location.replace('/pages/login');
  }
}

if (!customElements.get('nara-auth-form')) {
  customElements.define('nara-auth-form', NaraAuthForm);
}
if (!customElements.get('nara-account-space')) {
  customElements.define('nara-account-space', NaraAccountSpace);
}

function naraAuthBoot() {
  if (window.location.pathname.includes('/pages/reset-password')) {
    const root = document.querySelector('[data-supabase-url]');
    if (root) naraAuthCaptureRecovery(naraAuthConfig(root));
  }
  naraAuthUpdateHeader();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', naraAuthBoot);
} else {
  naraAuthBoot();
}
